import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  onAddCategory?: () => void;
  error?: boolean;
}

export function MultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options',
  onAddCategory,
  error,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Normalize selectedValues to always be a safe array of strings
  const values = Array.isArray(selectedValues)
    ? selectedValues
    : selectedValues
      ? [String(selectedValues)]
      : [];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (value: string) => {
    const nextValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
    onChange(nextValues);
  };

  const handleRemoveValue = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(values.filter((v) => v !== value));
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-left cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive'
        )}
      >
        <div className="flex flex-wrap gap-1.5 items-center">
          {values.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            values.map((val) => {
              const opt = options.find((o) => o.value === val);
              return (
                <Badge
                  key={val}
                  variant="secondary"
                  className="flex items-center gap-1 pl-2 pr-1 py-0.5 max-w-[150px] truncate"
                >
                  <span className="truncate">{opt ? opt.label : val}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleRemoveValue(val, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleRemoveValue(val, e as unknown as React.MouseEvent);
                      }
                    }}
                    className="rounded-full p-0.5 hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              );
            })
          )}
        </div>
        <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          <div className="space-y-0.5">
            {options.map((option) => {
              const isChecked = values.includes(option.value);
              return (
                <div
                  key={option.value}
                  onClick={() => handleToggleOption(option.value)}
                  className="relative flex w-full select-none items-center rounded-sm py-1.5 pl-3 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer gap-2"
                >
                  <div
                    className={cn(
                      'h-4 w-4 shrink-0 rounded border border-input flex items-center justify-center transition-colors',
                      isChecked ? 'bg-primary border-primary text-primary-foreground' : 'bg-transparent'
                    )}
                  >
                    {isChecked && <Check className="h-3 w-3 stroke-[3]" />}
                  </div>
                  <span className="pointer-events-none">{option.label}</span>
                </div>
              );
            })}
            {onAddCategory && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddCategory();
                  setIsOpen(false);
                }}
                className="flex w-full items-center rounded-sm py-1.5 px-3 text-sm font-medium text-primary hover:bg-accent text-left mt-1 border-t pt-2"
              >
                + Add Category
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
